import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

const Zodiac3D = (() => {
  let scene, camera, renderer, controls;
  let deviceControls = null;
  let animationId = null;
  let isARMode = false;
  let container = null;
  let clock = new THREE.Clock();

  const GOLD = new THREE.Color(0xab8c52);
  const GOLD_LIGHT = new THREE.Color(0xc9a84c);
  const TEXT_COLOR = new THREE.Color(0xf0ece4);
  const BG_COLOR = new THREE.Color(0x0a0a1a);

  const RASHI_NAMES_JP = ["メーシャ","ヴリシャバ","ミトゥナ","カルカ","シンハ","カンヤ","トゥラー","ヴリシュチカ","ダヌ","マカラ","クンバ","ミーナ"];
  const RASHI_NAMES_EN = ["Aries","Taurus","Gemini","Cancer","Leo","Virgo","Libra","Scorpio","Sagittarius","Capricorn","Aquarius","Pisces"];

  const ZODIAC_PATHS = {
    0: "M6 20C6 12 8 6 12 4C16 6 18 12 18 20 M12 4V12",
    1: "M8 8C6 4 10 2 12 4C14 2 18 4 16 8 M7 12A5 5 0 1 0 17 12A5 5 0 1 0 7 12",
    2: "M6 4H18 M6 20H18 M9 4V20 M15 4V20",
    3: "M18 9A5 5 0 0 0 8 9C8 9 8 12 12 12 M6 15A5 5 0 0 0 16 15C16 15 16 12 12 12",
    4: "M6 16A4 4 0 1 1 14 16C14 12 18 10 18 6A3 3 0 0 0 12 6",
    5: "M4 18V6L8 14V6L12 14V6 M12 14C12 18 16 20 18 16 M16 12L20 16",
    6: "M4 16H20 M12 16V8 M6 8C6 4 12 4 12 8 M12 8C12 4 18 4 18 8",
    7: "M4 18V6L8 14V6L12 14V6L16 18 M16 18L20 14 M18 18L20 14",
    8: "M4 20L20 4 M14 4H20V10 M8 12L16 12",
    9: "M4 4L10 16C10 20 14 22 16 18V8A3 3 0 1 1 22 8",
    10: "M4 10C6 8 8 8 10 10C12 12 14 12 16 10C18 8 20 8 22 10 M4 16C6 14 8 14 10 16C12 18 14 18 16 16C18 14 20 14 22 16",
    11: "M8 4C4 8 4 16 8 20 M16 4C20 8 20 16 16 20 M4 12H20",
  };

  const PLANET_NAMES_JP = {
    Sun: "太陽", Moon: "月", Mars: "火星", Mercury: "水星",
    Jupiter: "木星", Venus: "金星", Saturn: "土星", Rahu: "ラーフ", Ketu: "ケートゥ"
  };

  const PLANET_COLORS = {
    Sun: 0xffaa00, Moon: 0xccccff, Mars: 0xff4444,
    Mercury: 0x44ff88, Jupiter: 0xffcc44, Venus: 0xff88cc,
    Saturn: 0x8888aa, Rahu: 0x6644aa, Ketu: 0xaa4466
  };

  const SPHERE_RADIUS = 50;

  // 12星座の主要星（赤経ra度, 赤緯dec度, 等級mag）と結線パターン
  // 座標は J2000.0 epoch の実天文データに基づく
  const CONSTELLATION_DATA = {
    0: { // Aries
      stars: [
        { ra: 31.79, dec: 23.46, mag: 2.0 },  // α Hamal
        { ra: 28.66, dec: 20.81, mag: 2.6 },  // β Sheratan
        { ra: 28.38, dec: 19.29, mag: 3.9 },  // γ Mesarthim
        { ra: 47.37, dec: 19.73, mag: 3.6 },  // 41 Ari
      ],
      lines: [[2,1],[1,0],[0,3]]
    },
    1: { // Taurus
      stars: [
        { ra: 68.98, dec: 16.51, mag: 0.9 },  // α Aldebaran
        { ra: 81.57, dec: 28.61, mag: 1.7 },  // β Elnath
        { ra: 67.15, dec: 15.96, mag: 3.5 },  // ε Ain
        { ra: 64.95, dec: 15.63, mag: 3.4 },  // γ Hyadum I
        { ra: 84.41, dec: 21.14, mag: 3.0 },  // ζ Tau
        { ra: 60.79, dec: 12.49, mag: 3.7 },  // δ Hyadum II
      ],
      lines: [[5,3],[3,2],[2,0],[0,1],[0,4]]
    },
    2: { // Gemini
      stars: [
        { ra: 116.33, dec: 28.03, mag: 1.6 },  // β Pollux
        { ra: 113.65, dec: 31.89, mag: 1.9 },  // α Castor
        { ra: 99.43, dec: 16.40, mag: 1.9 },   // γ Alhena
        { ra: 100.98, dec: 25.13, mag: 3.1 },  // ε Mebsuta
        { ra: 95.74, dec: 22.51, mag: 3.3 },   // μ Tejat
        { ra: 93.72, dec: 22.51, mag: 3.4 },   // η Propus
      ],
      lines: [[1,0],[1,3],[3,5],[5,4],[0,2]]
    },
    3: { // Cancer
      stars: [
        { ra: 134.62, dec: 11.86, mag: 4.3 },  // α Acubens
        { ra: 124.13, dec: 9.19, mag: 3.5 },   // β Al Tarf
        { ra: 130.81, dec: 21.47, mag: 4.0 },  // γ Asellus Borealis
        { ra: 131.17, dec: 18.15, mag: 3.9 },  // δ Asellus Australis
        { ra: 131.67, dec: 28.77, mag: 4.0 },  // ι Cnc
      ],
      lines: [[1,3],[3,0],[3,2],[2,4]]
    },
    4: { // Leo
      stars: [
        { ra: 152.09, dec: 11.97, mag: 1.4 },  // α Regulus
        { ra: 177.26, dec: 14.57, mag: 2.1 },  // β Denebola
        { ra: 154.99, dec: 19.84, mag: 2.1 },  // γ Algieba
        { ra: 168.53, dec: 20.52, mag: 2.6 },  // δ Zosma
        { ra: 168.56, dec: 23.42, mag: 3.3 },  // θ Chertan
        { ra: 148.19, dec: 26.01, mag: 3.0 },  // ε Ras Elased
        { ra: 146.46, dec: 23.77, mag: 3.5 },  // μ Rasalas
      ],
      lines: [[0,2],[2,5],[5,6],[2,3],[3,4],[3,1]]
    },
    5: { // Virgo
      stars: [
        { ra: 201.30, dec: -11.16, mag: 1.0 },  // α Spica
        { ra: 195.54, dec: 10.96, mag: 2.8 },   // ε Vindemiatrix
        { ra: 190.42, dec: -1.45, mag: 2.7 },   // γ Porrima
        { ra: 184.98, dec: 3.40, mag: 3.4 },    // δ Auva
        { ra: 203.67, dec: -0.60, mag: 3.4 },   // ζ Heze
        { ra: 192.42, dec: -22.68, mag: 3.9 },  // θ Vir (far south — gives depth)
      ],
      lines: [[3,2],[2,0],[2,4],[4,1],[0,5]]
    },
    6: { // Libra
      stars: [
        { ra: 222.72, dec: -16.04, mag: 2.7 },  // α Zubenelgenubi
        { ra: 229.25, dec: -9.38, mag: 2.6 },   // β Zubeneschamali
        { ra: 239.11, dec: -25.28, mag: 3.9 },  // σ Brachium
        { ra: 233.88, dec: -14.79, mag: 3.9 },  // γ Lib
      ],
      lines: [[0,1],[0,3],[3,2],[1,3]]
    },
    7: { // Scorpius
      stars: [
        { ra: 247.35, dec: -26.43, mag: 1.0 },  // α Antares
        { ra: 240.08, dec: -22.62, mag: 2.3 },  // δ Dschubba
        { ra: 241.36, dec: -19.81, mag: 2.6 },  // β Acrab
        { ra: 263.40, dec: -37.10, mag: 1.6 },  // λ Shaula
        { ra: 264.33, dec: -43.00, mag: 1.9 },  // θ Sargas
        { ra: 252.54, dec: -34.29, mag: 2.3 },  // ε Sco
        { ra: 239.71, dec: -26.11, mag: 2.9 },  // σ Sco
      ],
      lines: [[2,1],[1,6],[6,0],[0,5],[5,3],[3,4]]
    },
    8: { // Sagittarius (Teapot asterism)
      stars: [
        { ra: 276.04, dec: -34.38, mag: 1.8 },  // ε Kaus Australis
        { ra: 283.82, dec: -26.30, mag: 2.0 },  // σ Nunki
        { ra: 284.68, dec: -29.88, mag: 2.6 },  // ζ Ascella
        { ra: 275.25, dec: -29.83, mag: 2.7 },  // δ Kaus Media
        { ra: 271.45, dec: -25.42, mag: 2.8 },  // λ Kaus Borealis
        { ra: 286.17, dec: -21.11, mag: 2.9 },  // φ Sgr
        { ra: 281.41, dec: -26.99, mag: 2.9 },  // τ Sgr
      ],
      lines: [[4,3],[3,0],[0,2],[2,1],[1,5],[1,6],[6,4]]
    },
    9: { // Capricornus
      stars: [
        { ra: 304.51, dec: -12.51, mag: 3.6 },  // α Algedi
        { ra: 305.25, dec: -14.78, mag: 3.1 },  // β Dabih
        { ra: 326.76, dec: -16.13, mag: 2.9 },  // δ Deneb Algedi
        { ra: 325.02, dec: -16.66, mag: 3.7 },  // γ Nashira
        { ra: 316.49, dec: -22.41, mag: 3.7 },  // ζ Cap
        { ra: 320.56, dec: -25.27, mag: 4.1 },  // ω Cap
      ],
      lines: [[0,1],[1,4],[4,5],[5,3],[3,2],[2,0]]
    },
    10: { // Aquarius
      stars: [
        { ra: 322.89, dec: -5.57, mag: 2.9 },   // β Sadalsuud
        { ra: 331.45, dec: -0.32, mag: 2.9 },   // α Sadalmelik
        { ra: 343.66, dec: -15.82, mag: 3.3 },  // δ Skat
        { ra: 339.39, dec: -7.58, mag: 3.8 },   // λ Aqr
        { ra: 311.92, dec: -9.50, mag: 3.8 },   // ε Albali
        { ra: 340.37, dec: -18.87, mag: 4.0 },  // 88 Aqr
      ],
      lines: [[4,0],[0,1],[1,3],[3,2],[2,5]]
    },
    11: { // Pisces
      stars: [
        { ra: 22.87, dec: 15.35, mag: 3.6 },   // η Psc
        { ra: 30.51, dec: 2.76, mag: 3.8 },    // α Alrescha
        { ra: 26.35, dec: 9.16, mag: 4.3 },    // ο Psc
        { ra: 6.80, dec: 7.59, mag: 4.1 },     // γ Psc (west fish)
        { ra: 1.69, dec: -6.01, mag: 4.4 },    // θ Psc
        { ra: 345.97, dec: -5.18, mag: 4.4 },  // κ Psc (far west)
        { ra: 12.28, dec: 24.58, mag: 4.5 },   // ι Psc (north)
      ],
      lines: [[5,4],[4,3],[3,6],[6,0],[0,2],[2,1]]
    },
  };

  function createTextSprite(text, opts = {}) {
    const fontSize = opts.fontSize || 64;
    const color = opts.color || '#c9a84c';
    const bgColor = opts.bgColor || null;
    const padding = opts.padding || 12;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const fontFamily = opts.fontFamily || '"Cormorant Garamond", "Shippori Mincho", serif';
    ctx.font = `${fontSize}px ${fontFamily}`;
    const metrics = ctx.measureText(text);
    const textWidth = metrics.width;

    canvas.width = Math.ceil(textWidth + padding * 2);
    canvas.height = Math.ceil(fontSize * 1.4 + padding * 2);

    if (bgColor) {
      ctx.fillStyle = bgColor;
      ctx.beginPath();
      ctx.roundRect(0, 0, canvas.width, canvas.height, 6);
      ctx.fill();
    }

    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;

    const spriteMat = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      sizeAttenuation: true
    });
    const sprite = new THREE.Sprite(spriteMat);
    const aspect = canvas.width / canvas.height;
    const scale = opts.scale || 5;
    sprite.scale.set(scale * aspect, scale, 1);
    return sprite;
  }

  function createStarField() {
    const count = 3000;
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const alphas = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = SPHERE_RADIUS * (0.85 + Math.random() * 0.15);

      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);

      sizes[i] = Math.random() * 2.5 + 0.5;
      alphas[i] = Math.random() * 0.7 + 0.3;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute('aAlpha', new THREE.BufferAttribute(alphas, 1));

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uPixelRatio: { value: window.devicePixelRatio }
      },
      vertexShader: `
        attribute float aSize;
        attribute float aAlpha;
        varying float vAlpha;
        uniform float uTime;
        uniform float uPixelRatio;
        void main() {
          vAlpha = aAlpha * (0.7 + 0.3 * sin(uTime * 1.5 + position.x * 0.1));
          vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = aSize * uPixelRatio * (80.0 / -mvPos.z);
          gl_Position = projectionMatrix * mvPos;
        }
      `,
      fragmentShader: `
        varying float vAlpha;
        void main() {
          float d = length(gl_PointCoord - vec2(0.5));
          if (d > 0.5) discard;
          float alpha = vAlpha * smoothstep(0.5, 0.1, d);
          gl_FragColor = vec4(0.94, 0.93, 0.89, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    return new THREE.Points(geometry, material);
  }

  function createEclipticRing() {
    const curve = new THREE.EllipseCurve(0, 0, SPHERE_RADIUS * 0.72, SPHERE_RADIUS * 0.72, 0, Math.PI * 2, false, 0);
    const points = curve.getPoints(128);
    const geometry = new THREE.BufferGeometry().setFromPoints(
      points.map(p => new THREE.Vector3(p.x, 0, p.y))
    );
    const material = new THREE.LineBasicMaterial({
      color: GOLD,
      transparent: true,
      opacity: 0.25
    });
    return new THREE.Line(geometry, material);
  }

  function createZodiacIcon(index) {
    const size = 256;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    // Icon area: top portion
    const iconSize = size * 0.4;
    const iconX = (size - iconSize) / 2;
    const iconY = size * 0.08;

    // Draw zodiac path with gold stroke + subtle glow
    const pathStr = ZODIAC_PATHS[index];
    if (pathStr) {
      ctx.save();
      ctx.translate(iconX, iconY);
      const scale = iconSize / 24;
      ctx.scale(scale, scale);

      const path = new Path2D(pathStr);

      // Subtle outer glow
      ctx.shadowColor = 'rgba(201, 168, 76, 0.4)';
      ctx.shadowBlur = 3;
      ctx.strokeStyle = '#c9a84c';
      ctx.lineWidth = 1.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke(path);

      // Sharper inner line
      ctx.shadowBlur = 0;
      ctx.strokeStyle = '#c9a84c';
      ctx.lineWidth = 0.8;
      ctx.stroke(path);

      ctx.restore();
    }

    // Thin separator line
    const lineY = iconY + iconSize + size * 0.06;
    ctx.beginPath();
    ctx.moveTo(size * 0.3, lineY);
    ctx.lineTo(size * 0.7, lineY);
    ctx.strokeStyle = 'rgba(171, 140, 82, 0.2)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // English name — elegant serif
    const nameY = lineY + size * 0.13;
    ctx.font = 'italic 300 28px "Cormorant Garamond", serif';
    ctx.fillStyle = 'rgba(201, 168, 76, 0.9)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(RASHI_NAMES_EN[index], size / 2, nameY);

    // Japanese name — mincho
    const jpY = nameY + size * 0.11;
    ctx.font = '400 16px "Shippori Mincho", serif';
    ctx.fillStyle = 'rgba(201, 168, 76, 0.45)';
    ctx.fillText(RASHI_NAMES_JP[index], size / 2, jpY);

    // Create sprite
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    const mat = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      sizeAttenuation: true
    });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(7, 7, 1);
    sprite.userData = { type: 'zodiac', index };
    return sprite;
  }

  function createZodiacSigns(rotationOffset) {
    const group = new THREE.Group();
    const r = SPHERE_RADIUS * 0.78;

    for (let i = 0; i < 12; i++) {
      const angleDeg = i * 30 + 15 + rotationOffset;
      const angleRad = THREE.MathUtils.degToRad(angleDeg);

      const x = r * Math.cos(angleRad);
      const z = r * Math.sin(angleRad);

      // Zodiac icon sprite
      const icon = createZodiacIcon(i);
      icon.position.set(x, 5, z);
      group.add(icon);

      // Segment divider line
      const divAngle = THREE.MathUtils.degToRad(i * 30 + rotationOffset);
      const lineGeo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(
          SPHERE_RADIUS * 0.68 * Math.cos(divAngle), 0,
          SPHERE_RADIUS * 0.68 * Math.sin(divAngle)
        ),
        new THREE.Vector3(
          SPHERE_RADIUS * 0.85 * Math.cos(divAngle), 0,
          SPHERE_RADIUS * 0.85 * Math.sin(divAngle)
        )
      ]);
      const lineMat = new THREE.LineBasicMaterial({
        color: GOLD,
        transparent: true,
        opacity: 0.1
      });
      group.add(new THREE.Line(lineGeo, lineMat));
    }

    return group;
  }

  function raDecToXYZ(ra, dec, r) {
    const raRad = THREE.MathUtils.degToRad(ra);
    const decRad = THREE.MathUtils.degToRad(dec);
    return new THREE.Vector3(
      r * Math.cos(decRad) * Math.cos(raRad),
      r * Math.sin(decRad),
      -r * Math.cos(decRad) * Math.sin(raRad)
    );
  }

  function createConstellations() {
    const group = new THREE.Group();
    const r = SPHERE_RADIUS * 0.9;

    for (let ci = 0; ci < 12; ci++) {
      const data = CONSTELLATION_DATA[ci];
      if (!data) continue;

      const starPositions = data.stars.map(s =>
        raDecToXYZ(s.ra, s.dec, r)
      );

      // Draw stars as bright spheres
      data.stars.forEach((s, si) => {
        const pos = starPositions[si];
        const starSize = Math.max(0.4, 1.5 - s.mag * 0.3);
        const starGeo = new THREE.SphereGeometry(starSize, 8, 8);
        const starMat = new THREE.MeshBasicMaterial({
          color: 0xf0ece4
        });
        const starMesh = new THREE.Mesh(starGeo, starMat);
        starMesh.position.copy(pos);
        group.add(starMesh);

        // Glow for bright stars (mag < 2)
        if (s.mag < 2) {
          const glowGeo = new THREE.SphereGeometry(starSize * 2.5, 8, 8);
          const glowMat = new THREE.MeshBasicMaterial({
            color: 0xf0ece4,
            transparent: true,
            opacity: 0.08
          });
          const glow = new THREE.Mesh(glowGeo, glowMat);
          glow.position.copy(pos);
          group.add(glow);
        }
      });

      // Draw constellation lines
      data.lines.forEach(([a, b]) => {
        const lineGeo = new THREE.BufferGeometry().setFromPoints([
          starPositions[a], starPositions[b]
        ]);
        const lineMat = new THREE.LineBasicMaterial({
          color: GOLD,
          transparent: true,
          opacity: 0.25
        });
        group.add(new THREE.Line(lineGeo, lineMat));
      });
    }

    return group;
  }

  function createPlanets(planetPositions, rotationOffset, highlightPlanet) {
    const group = new THREE.Group();
    const r = SPHERE_RADIUS * 0.5;

    // Sort and space planets to avoid overlap
    const posList = Object.keys(planetPositions).map(key => ({
      key,
      angleDeg: planetPositions[key].longitude + rotationOffset
    }));
    posList.sort((a, b) => a.angleDeg - b.angleDeg);
    for (let i = 1; i < posList.length; i++) {
      if (Math.abs(posList[i].angleDeg - posList[i - 1].angleDeg) < 12) {
        posList[i].angleDeg = posList[i - 1].angleDeg + 12;
      }
    }

    posList.forEach(({ key, angleDeg }) => {
      const angleRad = THREE.MathUtils.degToRad(angleDeg);
      const isHL = key === highlightPlanet;

      const x = r * Math.cos(angleRad);
      const z = r * Math.sin(angleRad);

      // Planet sphere
      const size = isHL ? 1.2 : 0.8;
      const sphereGeo = new THREE.SphereGeometry(size, 16, 16);
      const color = PLANET_COLORS[key] || 0xffffff;
      const sphereMat = new THREE.MeshBasicMaterial({ color });
      const sphere = new THREE.Mesh(sphereGeo, sphereMat);
      sphere.position.set(x, 0, z);
      sphere.userData = { type: 'planet', key };
      group.add(sphere);

      // Glow for highlighted planet
      if (isHL) {
        const glowGeo = new THREE.SphereGeometry(2.5, 16, 16);
        const glowMat = new THREE.MeshBasicMaterial({
          color: GOLD,
          transparent: true,
          opacity: 0.15
        });
        const glow = new THREE.Mesh(glowGeo, glowMat);
        glow.position.set(x, 0, z);
        glow.userData = { isGlow: true, parentKey: key };
        group.add(glow);

        // Outer glow ring
        const ringGeo = new THREE.RingGeometry(2.0, 3.0, 32);
        const ringMat = new THREE.MeshBasicMaterial({
          color: GOLD,
          transparent: true,
          opacity: 0.08,
          side: THREE.DoubleSide
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.position.set(x, 0, z);
        ring.rotation.x = -Math.PI / 2;
        ring.userData = { isGlowRing: true, parentKey: key };
        group.add(ring);
      }

      // Planet name label
      const label = createTextSprite(
        `${key} ${PLANET_NAMES_JP[key] || ''}`, {
          fontSize: isHL ? 40 : 32,
          color: isHL ? '#c9a84c' : '#f0ece4',
          scale: isHL ? 2.8 : 2.2
        }
      );
      label.position.set(x, isHL ? 3.5 : 2.5, z);
      group.add(label);
    });

    return group;
  }

  function createLagnaMarker(lagna, rotationOffset) {
    const group = new THREE.Group();
    const angleDeg = lagna.longitude + rotationOffset;
    const angleRad = THREE.MathUtils.degToRad(angleDeg);
    const r = SPHERE_RADIUS * 0.86;

    const x = r * Math.cos(angleRad);
    const z = r * Math.sin(angleRad);

    // Triangle marker
    const shape = new THREE.Shape();
    shape.moveTo(0, 1.5);
    shape.lineTo(-1, -0.5);
    shape.lineTo(1, -0.5);
    shape.closePath();

    const geo = new THREE.ShapeGeometry(shape);
    const mat = new THREE.MeshBasicMaterial({
      color: GOLD,
      side: THREE.DoubleSide
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(x, 0, z);
    mesh.lookAt(0, 0, 0);
    mesh.rotateX(Math.PI / 2);
    group.add(mesh);

    // ASC label
    const label = createTextSprite('ASC', {
      fontSize: 36,
      color: '#ab8c52',
      scale: 2
    });
    label.position.set(
      (r + 4) * Math.cos(angleRad),
      0,
      (r + 4) * Math.sin(angleRad)
    );
    group.add(label);

    return group;
  }

  function isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  function createARButton() {
    return null;

    /* AR button disabled
    if (!isTouchDevice()) return null;

    const btn = document.createElement('button');
    btn.id = 'ar-toggle-btn';
    btn.textContent = 'この星空を体験する';
    btn.style.cssText = `
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      padding: 12px 28px;
      background: rgba(171, 140, 82, 0.15);
      border: 1px solid rgba(171, 140, 82, 0.4);
      color: #c9a84c;
      font-family: "Noto Sans JP", sans-serif;
      font-size: 14px;
      border-radius: 30px;
      cursor: pointer;
      z-index: 20;
      pointer-events: auto;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      transition: all 0.3s ease;
    `;
    btn.addEventListener('mouseenter', () => {
      btn.style.background = 'rgba(171, 140, 82, 0.25)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'rgba(171, 140, 82, 0.15)';
    });
    return btn;
    AR button disabled */
  }

  function createTooltip() {
    const tip = document.createElement('div');
    tip.id = 'zodiac3d-tooltip';
    tip.style.cssText = `
      position: absolute;
      padding: 8px 16px;
      background: rgba(10, 10, 26, 0.9);
      border: 1px solid rgba(171, 140, 82, 0.3);
      border-radius: 6px;
      color: #c9a84c;
      font-family: "Noto Sans JP", sans-serif;
      font-size: 13px;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.2s;
      z-index: 20;
      white-space: nowrap;
    `;
    return tip;
  }

  // Raycast hover
  function setupInteraction(domElement, tooltipEl) {
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2(-999, -999);

    function onMove(e) {
      const rect = domElement.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    }

    function onLeave() {
      pointer.set(-999, -999);
      tooltipEl.style.opacity = '0';
    }

    domElement.addEventListener('mousemove', onMove);
    domElement.addEventListener('touchstart', onMove, { passive: true });
    domElement.addEventListener('touchmove', onMove, { passive: true });
    domElement.addEventListener('mouseleave', onLeave);
    domElement.addEventListener('touchend', onLeave);

    return { raycaster, pointer };
  }

  // DeviceOrientation "controls" (minimal implementation)
  function createDeviceOrientationControls(cam) {
    const state = {
      enabled: false,
      alpha: 0, beta: 0, gamma: 0,
      orient: window.orientation || 0
    };

    const zee = new THREE.Vector3(0, 0, 1);
    const euler = new THREE.Euler();
    const q0 = new THREE.Quaternion();
    const q1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));

    function onOrientation(e) {
      state.alpha = e.alpha || 0;
      state.beta = e.beta || 0;
      state.gamma = e.gamma || 0;
    }

    function onOrientChange() {
      state.orient = window.orientation || 0;
    }

    function connect() {
      window.addEventListener('deviceorientation', onOrientation);
      window.addEventListener('orientationchange', onOrientChange);
      state.enabled = true;
    }

    function disconnect() {
      window.removeEventListener('deviceorientation', onOrientation);
      window.removeEventListener('orientationchange', onOrientChange);
      state.enabled = false;
    }

    function update() {
      if (!state.enabled) return;

      const alpha = THREE.MathUtils.degToRad(state.alpha);
      const beta = THREE.MathUtils.degToRad(state.beta);
      const gamma = THREE.MathUtils.degToRad(state.gamma);
      const orient = THREE.MathUtils.degToRad(state.orient);

      euler.set(beta, alpha, -gamma, 'YXZ');
      cam.quaternion.setFromEuler(euler);
      cam.quaternion.multiply(q1);
      cam.quaternion.multiply(q0.setFromAxisAngle(zee, -orient));
    }

    return { connect, disconnect, update, state };
  }

  async function requestGyroPermission() {
    if (typeof DeviceOrientationEvent !== 'undefined' &&
        typeof DeviceOrientationEvent.requestPermission === 'function') {
      // iOS 13+
      const perm = await DeviceOrientationEvent.requestPermission();
      return perm === 'granted';
    }
    // Android / others
    return true;
  }

  function render(canvasId, planetPositions, lagna, highlightPlanet) {
    destroy();

    const canvas = document.getElementById(canvasId);
    if (!canvas) return;

    container = canvas.parentElement;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene
    scene = new THREE.Scene();
    scene.background = BG_COLOR;
    scene.fog = new THREE.FogExp2(0x0a0a1a, 0.005);

    // Camera
    camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 200);
    camera.position.set(0, 15, 45);

    // Renderer
    renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // Controls
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 15;
    controls.maxDistance = 80;
    controls.enableZoom = false;
    controls.enablePan = false;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.3;

    // Layout
    const lagnaRashiStart = lagna.rashiIndex * 30;
    const rotationOffset = -lagnaRashiStart;

    // Star field
    const stars = createStarField();
    scene.add(stars);

    // Ecliptic ring
    const ecliptic = createEclipticRing();
    scene.add(ecliptic);

    // Zodiac signs
    const zodiacGroup = createZodiacSigns(rotationOffset);
    scene.add(zodiacGroup);

    // Constellation star patterns
    const constellationGroup = createConstellations();
    scene.add(constellationGroup);

    // Planets
    const planetGroup = createPlanets(planetPositions, rotationOffset, highlightPlanet);
    scene.add(planetGroup);

    // Lagna marker
    const lagnaMarker = createLagnaMarker(lagna, rotationOffset);
    scene.add(lagnaMarker);

    // Ambient light (for MeshBasicMaterial not needed, but keeps things consistent)
    scene.add(new THREE.AmbientLight(0xffffff, 1));

    // Tooltip
    const tooltip = createTooltip();
    container.style.position = 'relative';
    container.appendChild(tooltip);

    // Interaction
    const { raycaster, pointer } = setupInteraction(renderer.domElement, tooltip);

    // AR button (gyroscope devices only)
    const arBtn = createARButton();
    if (arBtn) {
      container.parentElement.appendChild(arBtn);
      deviceControls = createDeviceOrientationControls(camera);

      arBtn.addEventListener('click', async () => {
        if (!isARMode) {
          const granted = await requestGyroPermission();
          if (!granted) {
            arBtn.textContent = 'ジャイロの許可が必要です';
            setTimeout(() => { arBtn.textContent = 'この星空を体験する'; }, 2000);
            return;
          }
          isARMode = true;
          controls.enabled = false;
          controls.autoRotate = false;
          deviceControls.connect();
          camera.position.set(0, 0, 0);
          arBtn.textContent = 'ドラッグモードに戻る';
          arBtn.style.background = 'rgba(171, 140, 82, 0.3)';
        } else {
          isARMode = false;
          deviceControls.disconnect();
          controls.enabled = true;
          controls.autoRotate = true;
          camera.position.set(0, 15, 45);
          camera.lookAt(0, 0, 0);
          arBtn.textContent = 'この星空を体験する';
          arBtn.style.background = 'rgba(171, 140, 82, 0.15)';
        }
      });
    }

    // Resize handler
    function onResize() {
      if (!container || !camera || !renderer) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    }
    window.addEventListener('resize', onResize);

    // Animation loop
    function animate() {
      animationId = requestAnimationFrame(animate);

      const elapsed = clock.getElapsedTime();

      // Update star twinkle
      if (stars.material.uniforms) {
        stars.material.uniforms.uTime.value = elapsed;
      }

      // Animate highlight glow
      planetGroup.children.forEach(child => {
        if (child.userData.isGlow) {
          child.material.opacity = 0.1 + Math.sin(elapsed * 2) * 0.08;
          child.scale.setScalar(1 + Math.sin(elapsed * 1.5) * 0.15);
        }
        if (child.userData.isGlowRing) {
          child.material.opacity = 0.05 + Math.sin(elapsed * 2 + 1) * 0.04;
        }
      });

      // Controls
      if (isARMode && deviceControls) {
        deviceControls.update();
      } else if (controls) {
        controls.update();
      }

      // Raycasting for hover
      raycaster.setFromCamera(pointer, camera);
      const intersectable = [];
      scene.traverse(obj => {
        if (obj.userData && (obj.userData.type === 'zodiac' || obj.userData.type === 'planet')) {
          intersectable.push(obj);
        }
      });

      const hits = raycaster.intersectObjects(intersectable, false);
      if (hits.length > 0) {
        const hit = hits[0].object;
        const rect = renderer.domElement.getBoundingClientRect();
        const screenPos = hits[0].point.clone().project(camera);
        const sx = (screenPos.x * 0.5 + 0.5) * rect.width;
        const sy = (-screenPos.y * 0.5 + 0.5) * rect.height;

        if (hit.userData.type === 'zodiac') {
          const idx = hit.userData.index;
          tooltip.textContent = `${RASHI_NAMES_EN[idx]} · ${RASHI_NAMES_JP[idx]}`;
        } else if (hit.userData.type === 'planet') {
          const key = hit.userData.key;
          tooltip.textContent = `${key} · ${PLANET_NAMES_JP[key] || ''}`;
        }
        tooltip.style.left = `${sx}px`;
        tooltip.style.top = `${sy - 40}px`;
        tooltip.style.opacity = '1';
        renderer.domElement.style.cursor = 'pointer';
      } else {
        tooltip.style.opacity = '0';
        renderer.domElement.style.cursor = 'default';
      }

      renderer.render(scene, camera);
    }

    clock.start();
    animate();
  }

  function destroy() {
    if (animationId) {
      cancelAnimationFrame(animationId);
      animationId = null;
    }
    if (deviceControls) {
      deviceControls.disconnect();
      deviceControls = null;
    }
    if (controls) {
      controls.dispose();
      controls = null;
    }
    if (renderer) {
      renderer.dispose();
      renderer = null;
    }
    if (scene) {
      scene.traverse(obj => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (obj.material.map) obj.material.map.dispose();
          obj.material.dispose();
        }
      });
      scene = null;
    }
    camera = null;
    isARMode = false;

    // Remove UI elements
    const tooltip = document.getElementById('zodiac3d-tooltip');
    if (tooltip) tooltip.remove();
    const arBtn = document.getElementById('ar-toggle-btn');
    if (arBtn) arBtn.remove();
  }

  // Expose globally for non-module scripts
  window.Zodiac3D = { render, destroy };

  return { render, destroy };
})();

export default Zodiac3D;
